import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';

import { AuthScreen, SubmitButton } from '@/components/AuthScreen';
import { Field } from '@/components/Field';
import { useT } from '@/i18n/context';
import { ApiError, requestPasswordReset } from '@/lib/api';

export function ForgotPasswordPage() {
  const t = useT();
  const [email, setEmail] = useState('');
  const request = useMutation({ mutationFn: requestPasswordReset });
  const failure = request.error instanceof ApiError ? request.error : null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    request.mutate(email);
  };

  return (
    <AuthScreen
      title={t('auth.forgot_title')}
      subtitle={t('auth.forgot_subtitle')}
      error={failure && failure.status !== 422 ? failure.message : null}
      footer={
        <Link to="/login" className="underline">
          {t('auth.back_to_sign_in')}
        </Link>
      }
    >
      {request.isSuccess ? (
        // The API answers identically whether or not the address is registered,
        // so this screen must not imply an account was found either.
        <p role="status" className="rounded-lg bg-current/5 p-4 text-sm">
          {request.data.message}
        </p>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <Field
            label={t('auth.email')}
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            error={failure?.fieldErrors.email}
            onChange={(event) => setEmail(event.target.value)}
          />

          <SubmitButton pending={request.isPending}>
            {request.isPending ? t('auth.sending') : t('auth.send_link')}
          </SubmitButton>
        </form>
      )}
    </AuthScreen>
  );
}
